// =============================================================================
// hooks/kanban/useMoveCard.ts — Mutation para mover card entre colunas.
//
// Otimismo UI (F55-S08 — board paginado por estágio):
//   - tira o card da coluna de origem e insere na de destino na ordem do
//     servidor (priority DESC, updated_at DESC, id DESC — o move atualiza
//     updated_at, então o card sobe para o topo da sua faixa de prioridade);
//   - contador: origem −1, destino +1;
//   - rollback completo das duas colunas E do contador em erro (incluindo 422);
//   - ao terminar, revalida as duas colunas e a contagem.
// =============================================================================

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ApiError, api } from '../../lib/api';

import { applyMoveToCounts, insertCard, movedCard, removeCard } from './board';
import type { StageCards, StageCounts } from './board';
import type { KanbanFilters, MoveCardPayload, MoveCardResponse } from './types';
import { KANBAN_COUNTS_KEY, KANBAN_STAGE_CARDS_KEY } from './useKanbanCards';

export interface MoveCardContext {
  fromStageId: string;
  previousFrom: StageCards | undefined;
  previousTo: StageCards | undefined;
  previousCounts: StageCounts | undefined;
}

export interface UseMoveCardOptions {
  /** Filtros NORMALIZADOS do board (`useKanbanBoard().filters`) — mesma chave de cache. */
  filters: KanbanFilters;
  onInvalidTransition?: (cardId: string, targetStageId: string) => void;
  onError?: (error: Error) => void;
  onSuccess?: () => void;
}

/**
 * Mutation para mover um card entre colunas.
 *
 * Erro 422: transição inválida conforme matriz do F1-S13 → onInvalidTransition.
 */
export function useMoveCard({
  filters,
  onInvalidTransition,
  onError,
  onSuccess,
}: UseMoveCardOptions) {
  const queryClient = useQueryClient();

  return useMutation<MoveCardResponse, Error, MoveCardPayload, MoveCardContext>({
    mutationFn: ({ card, targetStageId }) =>
      api.post<MoveCardResponse>(`/api/kanban/cards/${card.id}/move`, {
        // Backend (kanban/schemas.ts moveCardBodySchema) espera toStageId em camelCase.
        toStageId: targetStageId,
      }),

    onMutate: async ({ card, targetStageId }) => {
      const fromKey = KANBAN_STAGE_CARDS_KEY(filters, card.stageId);
      const toKey = KANBAN_STAGE_CARDS_KEY(filters, targetStageId);
      const countsKey = KANBAN_COUNTS_KEY(filters);

      // Cancela fetches em curso para não sobrescrever o estado otimista.
      await Promise.all([
        queryClient.cancelQueries({ queryKey: fromKey }),
        queryClient.cancelQueries({ queryKey: toKey }),
        queryClient.cancelQueries({ queryKey: countsKey }),
      ]);

      const context: MoveCardContext = {
        fromStageId: card.stageId,
        previousFrom: queryClient.getQueryData<StageCards>(fromKey),
        previousTo: queryClient.getQueryData<StageCards>(toKey),
        previousCounts: queryClient.getQueryData<StageCounts>(countsKey),
      };

      queryClient.setQueryData<StageCards>(fromKey, (old) =>
        old ? removeCard(old, card.id) : old,
      );
      queryClient.setQueryData<StageCards>(toKey, (old) =>
        old ? insertCard(old, movedCard(card, targetStageId, new Date())) : old,
      );
      queryClient.setQueryData<StageCounts>(countsKey, (old) =>
        old ? applyMoveToCounts(old, card.stageId, targetStageId) : old,
      );

      return context;
    },

    onError: (error, { card, targetStageId }, context) => {
      if (context) {
        queryClient.setQueryData(
          KANBAN_STAGE_CARDS_KEY(filters, context.fromStageId),
          context.previousFrom,
        );
        queryClient.setQueryData(
          KANBAN_STAGE_CARDS_KEY(filters, targetStageId),
          context.previousTo,
        );
        queryClient.setQueryData(KANBAN_COUNTS_KEY(filters), context.previousCounts);
      }

      // 422 = transição inválida conforme matriz do F1-S13
      if (error instanceof ApiError && error.status === 422) {
        onInvalidTransition?.(card.id, targetStageId);
        return;
      }

      onError?.(error);
    },

    onSuccess: () => {
      onSuccess?.();
    },

    onSettled: (_data, _error, { card, targetStageId }) => {
      // Sincroniza com o servidor: as duas colunas e a contagem.
      void queryClient.invalidateQueries({
        queryKey: KANBAN_STAGE_CARDS_KEY(filters, card.stageId),
      });
      void queryClient.invalidateQueries({
        queryKey: KANBAN_STAGE_CARDS_KEY(filters, targetStageId),
      });
      void queryClient.invalidateQueries({ queryKey: KANBAN_COUNTS_KEY(filters) });
    },
  });
}
